const delay = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

function createBoxMeshStl(name, size = 26, height = 34) {
  const half = size / 2;
  const z = height;
  const points = {
    b0: [-half, -half, 0],
    b1: [half, -half, 0],
    b2: [half, half, 0],
    b3: [-half, half, 0],
    t0: [-half, -half, z],
    t1: [half, -half, z],
    t2: [half, half, z],
    t3: [-half, half, z]
  };

  const triangles = [
    ['bottom', [0, 0, -1], points.b0, points.b2, points.b1],
    ['bottom', [0, 0, -1], points.b0, points.b3, points.b2],
    ['top', [0, 0, 1], points.t0, points.t1, points.t2],
    ['top', [0, 0, 1], points.t0, points.t2, points.t3],
    ['front', [0, -1, 0], points.b0, points.b1, points.t1],
    ['front', [0, -1, 0], points.b0, points.t1, points.t0],
    ['right', [1, 0, 0], points.b1, points.b2, points.t2],
    ['right', [1, 0, 0], points.b1, points.t2, points.t1],
    ['back', [0, 1, 0], points.b2, points.b3, points.t3],
    ['back', [0, 1, 0], points.b2, points.t3, points.t2],
    ['left', [-1, 0, 0], points.b3, points.b0, points.t0],
    ['left', [-1, 0, 0], points.b3, points.t0, points.t3]
  ];

  const lines = [`solid ${name}`];
  for (const [, normal, a, b, c] of triangles) {
    lines.push(`  facet normal ${normal.join(' ')}`);
    lines.push('    outer loop');
    lines.push(`      vertex ${a.join(' ')}`);
    lines.push(`      vertex ${b.join(' ')}`);
    lines.push(`      vertex ${c.join(' ')}`);
    lines.push('    endloop');
    lines.push('  endfacet');
  }
  lines.push(`endsolid ${name}`);
  return lines.join('\n');
}

function createCylinderStl(name, radius = 24, height = 5, segments = 48) {
  const lines = [`solid ${name}`];
  const topCenter = [0, 0, height];
  const bottomCenter = [0, 0, 0];

  for (let i = 0; i < segments; i += 1) {
    const a = (i / segments) * Math.PI * 2;
    const b = ((i + 1) / segments) * Math.PI * 2;
    const p1 = [Math.cos(a) * radius, Math.sin(a) * radius, 0];
    const p2 = [Math.cos(b) * radius, Math.sin(b) * radius, 0];
    const p3 = [Math.cos(a) * radius, Math.sin(a) * radius, height];
    const p4 = [Math.cos(b) * radius, Math.sin(b) * radius, height];
    const nx = Math.cos((a + b) / 2).toFixed(5);
    const ny = Math.sin((a + b) / 2).toFixed(5);

    lines.push(`  facet normal ${nx} ${ny} 0`);
    lines.push('    outer loop');
    lines.push(`      vertex ${p1.join(' ')}`);
    lines.push(`      vertex ${p2.join(' ')}`);
    lines.push(`      vertex ${p4.join(' ')}`);
    lines.push('    endloop');
    lines.push('  endfacet');
    lines.push(`  facet normal ${nx} ${ny} 0`);
    lines.push('    outer loop');
    lines.push(`      vertex ${p1.join(' ')}`);
    lines.push(`      vertex ${p4.join(' ')}`);
    lines.push(`      vertex ${p3.join(' ')}`);
    lines.push('    endloop');
    lines.push('  endfacet');
    lines.push('  facet normal 0 0 1');
    lines.push('    outer loop');
    lines.push(`      vertex ${topCenter.join(' ')}`);
    lines.push(`      vertex ${p3.join(' ')}`);
    lines.push(`      vertex ${p4.join(' ')}`);
    lines.push('    endloop');
    lines.push('  endfacet');
    lines.push('  facet normal 0 0 -1');
    lines.push('    outer loop');
    lines.push(`      vertex ${bottomCenter.join(' ')}`);
    lines.push(`      vertex ${p2.join(' ')}`);
    lines.push(`      vertex ${p1.join(' ')}`);
    lines.push('    endloop');
    lines.push('  endfacet');
  }

  lines.push(`endsolid ${name}`);
  return lines.join('\n');
}

function createMockStl({ mode }) {
  const base = createCylinderStl('smart_base', mode === 'hd' ? 28 : 24, 5, mode === 'hd' ? 72 : 40);
  const body = createBoxMeshStl('ai_imagined_body', mode === 'hd' ? 24 : 22, mode === 'hd' ? 33 : 28)
    .replaceAll('vertex ', 'vertex ')
    .replaceAll(' 0\n', ' 5\n');
  return [
    `solid image_to_stl_generator_${mode}`,
    `  facet normal 0 0 1`,
    '    outer loop',
    `      vertex -1 -1 40`,
    `      vertex 1 -1 40`,
    `      vertex 0 1 40`,
    '    endloop',
    '  endfacet',
    base.replace(/^solid smart_base\n/, '').replace(/\nendsolid smart_base$/, ''),
    body.replace(/^solid ai_imagined_body\n/, '').replace(/\nendsolid ai_imagined_body$/, ''),
    `endsolid image_to_stl_generator_${mode}`
  ].join('\n');
}

export async function simulateStlGeneration({ file, mode, onProgress }) {
  const steps =
    mode === 'hd'
      ? [
          [12, 'Reading the silhouette and depth cues...'],
          [28, 'AI is imagining the backside of your object...'],
          [47, 'Building watertight geometry...'],
          [68, 'Optimizing the print-ready base...'],
          [86, 'Smoothing mesh details for STL export...'],
          [100, 'Final STL is ready.']
        ]
      : [
          [18, 'Reading the silhouette and depth cues...'],
          [42, 'AI is imagining the backside of your object...'],
          [72, 'Optimizing the print-ready base...'],
          [100, 'Final STL is ready.']
        ];

  for (const [progress, message] of steps) {
    onProgress({ progress, message });
    await delay(mode === 'hd' ? 620 : 420);
  }

  const stl = createMockStl({ mode });
  const blob = new Blob([stl], { type: 'model/stl' });
  const safeName = (file?.name || 'image-to-stl')
    .replace(/\.[^.]+$/, '')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();

  return {
    fileName: `${safeName || 'image-to-stl'}-${mode}.stl`,
    blob,
    url: URL.createObjectURL(blob),
    downloadUrl: URL.createObjectURL(blob),
    objectUrl: true,
    storage: 'browser-mock',
    stats: {
      vertices: mode === 'hd' ? '6,912' : '3,712',
      faces: mode === 'hd' ? '2,304' : '1,240',
      mode: mode === 'hd' ? 'HD Track' : 'Fast Track',
      ttl: '24 hours'
    }
  };
}

export async function generateStlWithApi({ file, prompt, mode, onProgress }) {
  onProgress({ progress: 10, message: 'Uploading image to the edge API...' });

  const formData = new FormData();
  formData.append('image', file);
  formData.append('prompt', prompt || '');
  formData.append('mode', mode);

  const response = await fetch('/api/generate', {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: formData
  });

  onProgress({ progress: 58, message: 'Writing the generated STL to Cloudflare R2...' });

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.error || `Generation API failed with HTTP ${response.status}.`);
  }

  onProgress({ progress: 90, message: 'Preparing R2-backed STL preview...' });

  return {
    fileName: payload.fileName,
    url: payload.previewUrl,
    downloadUrl: payload.downloadUrl || payload.previewUrl,
    objectUrl: false,
    storage: payload.storage || 'cloudflare-r2',
    stats: payload.stats
  };
}
